<template>
  <div
    v-if="visible"
    class="overlay"
    role="dialog"
    aria-modal="true"
    @keydown.esc="emitClose"
  >
    <div class="dialog">
      <header class="dialog-header">
        <h2 class="title">
          Fehlzeiten – {{ studentName }}
        </h2>
        <button
          class="btn"
          type="button"
          @click="emitClose"
        >
          Schließen
        </button>
      </header>

      <section class="dialog-body">
        <p class="muted meta mt-0 mb-3">
          Zeitraum: {{ formatDate(from) }} – {{ formatDate(to) }}
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>

        <p
          v-else-if="loading"
          class="mt-12"
        >
          Lädt Diagrammdaten …
        </p>

        <p
          v-else-if="items.length === 0"
          class="notice mt-12"
          role="status"
        >
          Keine Daten für das Diagramm vorhanden.
        </p>

        <div
          v-else
          class="chart-wrapper mt-12"
        >
          <div class="toolbar">
            <div
              class="group-toggle"
              role="group"
              aria-label="Gruppierung"
            >
              <button
                class="btn"
                :class="{'btn-primary': groupBy === 'month'}"
                :aria-pressed="groupBy === 'month'"
                @click="setGroup('month')"
              >
                Monate
              </button>
              <button
                class="btn"
                :class="{'btn-primary': groupBy === 'week'}"
                :aria-pressed="groupBy === 'week'"
                @click="setGroup('week')"
              >
                Kalenderwochen
              </button>
            </div>
          </div>

          <div class="legend">
            <span><span class="legend-swatch sw-total" />Fehlttage</span>
            <span><span class="legend-swatch sw-unexcused" />unentschuldigt</span>
            <span><span class="legend-swatch sw-ill" />Krankheit/Arztbesuch</span>
            <span><span class="legend-swatch sw-other" />andere Gründe</span>
          </div>

          <div class="chart-area">
            <!-- Y-Achse -->
            <div class="y-axis">
              <div class="y-title muted">
                Tage
              </div>
              <div
                class="y-scale"
                :style="{ height: chartHeight }"
                aria-hidden="true"
              >
                <div
                  v-for="t in yTicks"
                  :key="t"
                  class="y-tick"
                  :style="{ bottom: heightPct(t) }"
                >
                  <span class="y-tick-label">{{ t }}</span>
                  <span class="y-tick-mark" />
                </div>
              </div>
            </div>

            <!-- Plot-Bereich mit X-Achse (Linie) -->
            <div
              ref="plotScroll"
              class="plot-area"
            >
              <div
                class="chart-grid"
                role="img"
                :aria-label="`Säulenpaare, gruppiert nach ${groupByLabel}`"
              >
                <div
                  v-for="p in items"
                  :key="p.periodKey"
                  class="col"
                  :aria-label="`Periode ${p.periodLabel}`"
                >
                  <div
                    class="pair-row"
                    :style="{ height: chartHeight }"
                  >
                    <!-- Paar 1: Gesamt / Unentschuldigt -->
                    <div class="pair">
                      <div
                        class="bar bar-thin"
                        role="presentation"
                        :aria-label="`gesamt in ${p.periodLabel}`"
                      >
                        <div
                          class="seg seg-total"
                          :style="{ height: heightPct(p.days.totalReal) }"
                        />
                        <div
                          class="seg seg-total-comp"
                          :style="{ height: heightPct(p.days.totalComputed) }"
                        />
                      </div>
                      <div
                        class="bar bar-thin"
                        role="presentation"
                        :aria-label="`unentschuldigt in ${p.periodLabel}`"
                      >
                        <div
                          class="seg seg-unexcused"
                          :style="{ height: heightPct(p.days.unexcusedReal) }"
                        />
                        <div
                          class="seg seg-unexcused-comp"
                          :style="{ height: heightPct(p.days.unexcusedComputed) }"
                        />
                      </div>
                    </div>

                    <!-- Paar 2: Entschuldigt Kr./Unf. / Entschuldigt andere -->
                    <div class="pair">
                      <div
                        class="bar bar-thin"
                        role="presentation"
                        :aria-label="`Kr./Unf. in ${p.periodLabel}`"
                      >
                        <div
                          class="seg seg-ill"
                          :style="{
                            height: heightPct(p.days.excusedIllnessAccidentReal)
                          }"
                        />
                        <div
                          class="seg seg-ill-comp"
                          :style="{
                            height: heightPct(p.days.excusedIllnessAccidentComputed)
                          }"
                        />
                      </div>
                      <div
                        class="bar bar-thin"
                        role="presentation"
                        :aria-label="`andere in ${p.periodLabel}`"
                      >
                        <div
                          class="seg seg-other"
                          :style="{ height: heightPct(p.days.excusedOtherReal) }"
                        />
                        <div
                          class="seg seg-other-comp"
                          :style="{ height: heightPct(p.days.excusedOtherComputed) }"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    class="x-label"
                    :title="xLabelTitle(p)"
                  >
                    <div class="label-main">
                      {{ p.periodLabel }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Monatliche Kalenderansicht -->
        <div
          class="calendar-wrapper mt-12"
          aria-label="Monatliche Kalenderansicht"
        >
          <div class="cal-legend">
            <span><span class="legend-swatch cal-full cal-unexcused" />Fehltag (unentschuldigt)</span>
            <span><span class="legend-swatch cal-full cal-excused" />Fehltag (entschuldigt)</span>
            <span><span class="legend-swatch cal-partial cal-unexcused" />Fehlzeit (unentschuldigt)</span>
            <span><span class="legend-swatch cal-partial cal-excused" />Fehlzeit (entschuldigt)</span>
          </div>

          <p
            v-if="calendarLoading"
            class="mt-12"
          >
            Lädt Tagesdaten …
          </p>
          <p
            v-else-if="Object.keys(daily.byDate).length === 0"
            class="notice mt-12"
            role="status"
          >
            Keine Tagesdaten für den Zeitraum vorhanden.
          </p>

          <div
            v-else
            class="cal-months"
          >
            <div
              v-for="m in months"
              :key="m.key"
              class="cal-month"
            >
              <div class="cal-header">
                {{ m.label }}
              </div>

              <div class="cal-body">
                <!-- Kalenderwochen-Spalte links -->
                <div
                  class="cal-weeknums"
                  aria-hidden="true"
                >
                  <div class="cal-weeknum-header">
                    KW
                  </div>
                  <div
                    v-for="(w, idx) in m.weekNumbers"
                    :key="`kw-${m.key}-${idx}`"
                    class="cal-weeknum-cell"
                  >
                    {{ w }}
                  </div>
                </div>

                <!-- Wochentage + Tagesraster -->
                <div class="cal-main">
                  <div
                    class="cal-weekdays"
                    aria-hidden="true"
                  >
                    <span>Mo</span><span>Di</span><span>Mi</span><span>Do</span><span>Fr</span><span>Sa</span><span>So</span>
                  </div>
                  <div
                    class="cal-grid"
                    role="grid"
                    :aria-label="`Kalender ${m.label}`"
                  >
                    <div
                      v-for="i in m.leading"
                      :key="`l-${m.key}-${i}`"
                      class="cal-day pad"
                      role="gridcell"
                      aria-hidden="true"
                    />
                    <div
                      v-for="d in m.days"
                      :key="d.key"
                      class="cal-day"
                      :class="dayClasses(d.date)"
                      role="gridcell"
                      :aria-label="formatDate(d.date)"
                      :title="formatDate(d.date)"
                    >
                      <span class="cal-day-num">{{ d.day }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer class="dialog-footer">
        <button
          class="btn btn-primary"
          type="button"
          @click="emitClose"
        >
          OK
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import {onMounted, ref, watch, computed, nextTick} from 'vue';
import api from '@/services/api';

const props = defineProps({
  visible: {type: Boolean, required: true},
  studentId: {type: [Number, String], required: true},
  studentName: {type: String, required: true},
  from: {type: String, required: true},
  to: {type: String, required: true},
  schoolYearCode: {type: String, required: true}
});

const emit = defineEmits(['close']);

const loading = ref(false);
const error = ref('');
const items = ref([]);
const groupBy = ref('month');

const calendarLoading = ref(false);
const daily = ref({byDate: {}});

const months = computed(() => buildMonths(props.from, props.to));

const groupByLabel = computed(() => (groupBy.value === 'week' ? 'Kalenderwoche' : 'Monat'));

const plotScroll = ref(null);

async function scrollChartToRight() {
  await nextTick();
  requestAnimationFrame(() => {
    const el = plotScroll.value;
    if (!el) return;

    el.scrollTo({
      left: el.scrollWidth,
      behavior: 'smooth'
    });
  });
}

function setGroup(v) {
  if (v !== groupBy.value) {
    groupBy.value = v;
    load();
  }
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', {year: 'numeric', month: '2-digit', day: '2-digit'}).format(d);
}

function formatHours(value) {
  const v = Number(value) || 0;
  return v.toFixed(1).replace('.', ',');
}

// Feste Y-Skala (Tage): 0..20
const Y_MAX = 23;
const chartHeight = '160px';
const yTicks = [0, 5, 10, 15, 20];

// Fixe Maximalhöhe für Balkenskalierung
const maxBar = computed(() => Y_MAX);

function heightPct(value) {
  const v = Number(value) || 0;
  return `${Math.max(0, (v / maxBar.value) * 100)}%`;
}

function xLabelTitle(p) {
  return `Ø Pflichtstunden/Tag: ${formatHours(p.avgOfferHoursPerDay)} h • Zusatz-Fehltage: ${p.absenceQuotientDays}`;
}

/* Kalender – Hilfsfunktionen */
function pad2(n) {
  return String(n).padStart(2, '0');
}

// ISO-Kalenderwoche (Montag als Wochenanfang)
function getISOWeek(dateObj) {
  const d = new Date(Date.UTC(
      dateObj.getUTCFullYear(),
      dateObj.getUTCMonth(),
      dateObj.getUTCDate()
  ));
  const dayNum = d.getUTCDay() || 7; // 1=Mo … 7=So
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const diffDays = Math.round((d - yearStart) / 86400000) + 1;
  return Math.ceil(diffDays / 7);
}

function ymd(dateObj) {
  const y = dateObj.getUTCFullYear();
  const m = dateObj.getUTCMonth() + 1;
  const d = dateObj.getUTCDate();
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

function parseISODate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function buildMonths(from, to) {
  const fromDate = parseISODate(from);
  const toDate = parseISODate(to);
  if (!fromDate || !toDate) return [];

  // Cursor jeweils auf den Monatsersten, inklusive beider Monatsgrenzen
  const start = new Date(Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), 1));
  const end = new Date(Date.UTC(toDate.getUTCFullYear(), toDate.getUTCMonth(), 1));

  const months = [];
  const cursor = new Date(start);
  const fmt = new Intl.DateTimeFormat('de-DE', {month: 'long', year: 'numeric', timeZone: 'UTC'});

  while (cursor <= end) {
    const y = cursor.getUTCFullYear();
    const m = cursor.getUTCMonth();
    const first = new Date(Date.UTC(y, m, 1));
    const dim = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const wd = first.getUTCDay(); // 0 = So … 6 = Sa
    const leading = (wd + 6) % 7; // Montag = 0

    const key = `${y}-${pad2(m + 1)}`;
    const days = [];
    for (let d = 1; d <= dim; d++) {
      const dateObj = new Date(Date.UTC(y, m, d));
      days.push({
        day: d,
        date: ymd(dateObj),
        key: `${key}-${pad2(d)}`
      });
    }

    // Anzahl Kalenderzeilen und zugehörige Kalenderwochen berechnen
    const totalCells = leading + dim;
    const rows = Math.ceil(totalCells / 7);
    const weekNumbers = [];
    for (let r = 0; r < rows; r++) {
      const cellIndex = r * 7; // Index im Gesamtraster (0-basiert)
      const dayNum = cellIndex - leading + 1;
      let refDate;
      if (dayNum < 1) {
        refDate = new Date(Date.UTC(y, m, 1));
      } else if (dayNum > dim) {
        refDate = new Date(Date.UTC(y, m, dim));
      } else {
        refDate = new Date(Date.UTC(y, m, dayNum));
      }
      weekNumbers.push(getISOWeek(refDate));
    }

    months.push({
      key,
      label: fmt.format(first),
      leading,
      days,
      weekNumbers
    });

    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months;
}

function dayClasses(ymdStr) {
  const info = daily.value.byDate?.[ymdStr];

  const hasAbsence = !!info?.full || !!info?.partial;
  const status = info?.status || null;

  // Fallback: falls Backend (noch) keinen Status liefert, weiterhin rot markieren
  const effectiveStatus = hasAbsence && !status ? 'unexcused' : status;

  return {
    'is-full': !!info?.full,
    'is-partial': !!info?.partial,
    'is-excused': effectiveStatus === 'excused',
    'is-unexcused': effectiveStatus === 'unexcused'
  };
}

function normalizeCalendarData(data) {
  const out = {};
  if (!data) return out;

  const ensure = (d) => {
    if (!d) return null;
    out[d] = out[d] || {full: false, partial: false, status: null};
    return out[d];
  };

  // Neu (Backend v2): getrennte Arrays nach Entschuldigt/Unentschuldigt
  // Erwartet z.B.: absenceDaysExcused, absenceDaysUnexcused, absenceTimesExcused, absenceTimesUnexcused
  if (typeof data === 'object' && !Array.isArray(data)) {
    const setMany = (arr, kind, status) => {
      if (!Array.isArray(arr)) return;
      for (const d of arr) {
        if (typeof d !== 'string' || !d) continue;
        const cell = ensure(d);
        if (!cell) continue;
        cell[kind] = true;

        // Status nur setzen, wenn eindeutig
        if (status) cell.status = status;
      }
    };

    setMany(data.absenceDaysExcused, 'full', 'excused');
    setMany(data.absenceDaysUnexcused, 'full', 'unexcused');
    setMany(data.absenceTimesExcused, 'partial', 'excused');
    setMany(data.absenceTimesUnexcused, 'partial', 'unexcused');

    // Wenn wir damit schon etwas gefüllt haben, sind wir fertig.
    if (Object.keys(out).length > 0) return out;

    // Alt (Backend v1): nur "gesamt" ohne Status
    // -> bleibt nutzbar, wird im dayClasses-Fallback rot markiert
    setMany(data.absenceDays, 'full', null);
    setMany(data.absenceTimes, 'partial', null);

    if (Object.keys(out).length > 0) return out;
  }

  // Form 2 (Fallback): Array von Zeilen mit Feldern
  const list = Array.isArray(data) ? data : (Array.isArray(data?.rows) ? data.rows : []);
  for (const r of list) {
    const d = r?.date ?? r?.day ?? r?.d ?? r?.dateYmd ?? null;
    if (!d) continue;

    const hours = Number(r?.hours ?? r?.absenceHours ?? r?.absenceHoursTotal ?? 0);
    const days = Number(r?.days ?? r?.absenceDays ?? 0);
    const fullFlag = Boolean(r?.fullDay ?? r?.isFullDay ?? r?.type === 'day' ?? r?.kind === 'day' ?? days >= 1);
    const partialFlag = Boolean(r?.partial ?? r?.type === 'time' ?? r?.kind === 'time' ?? hours > 0);

    const cell = ensure(String(d));
    if (!cell) continue;

    if (fullFlag) cell.full = true;
    if (partialFlag) cell.partial = true;

    const raw = (r?.status ?? '').toString().toLowerCase();
    if (raw === 'excused' || raw === 'entschuldigt') cell.status = 'excused';
    if (raw === 'unexcused' || raw === 'unentschuldigt') cell.status = 'unexcused';
  }

  return out;
}

async function loadCalendar() {
  if (!props.visible) return;
  calendarLoading.value = true;
  try {
    const params = {
      studentId: props.studentId,
      from: props.from || undefined,
      to: props.to || undefined,
      schoolYearCode: props.schoolYearCode || undefined
    };
    const {data} = await api.get('/reports/absence-days/calendar', {params});
    daily.value.byDate = normalizeCalendarData(data);
  } catch (e) {
    // stiller Fehlerfall: Kalender bleibt leer
    daily.value.byDate = {};
  } finally {
    calendarLoading.value = false;
  }
}

async function load() {
  if (!props.visible) return;

  loading.value = true;
  error.value = '';
  items.value = [];

  try {
    const params = {
      studentId: props.studentId,
      from: props.from || undefined,
      to: props.to || undefined,
      schoolYearCode: props.schoolYearCode || undefined,
      groupBy: groupBy.value
    };

    const {data} = await api.get('/reports/absence-days/chart', {params});
    const list = Array.isArray(data) ? data : [];

    items.value = list.map((p) => ({
      periodKey: p.periodKey,
      periodLabel: p.periodLabel,
      offerDays: p.offerDays,
      offerHoursTotal: p.offerHoursTotal,
      avgOfferHoursPerDay: p.avgOfferHoursPerDay,
      days: {
        // Paar 1
        totalReal: Number(p.days?.totalReal ?? 0),
        totalComputed: Number(p.days?.totalComputed ?? 0),
        unexcusedReal: Number(p.days?.unexcusedReal ?? 0),
        unexcusedComputed: Number(p.days?.unexcusedComputed ?? 0),
        // Paar 2
        excusedIllnessAccidentReal: Number(p.days?.excusedIllnessAccidentReal ?? 0),
        excusedIllnessAccidentComputed: Number(p.days?.excusedIllnessAccidentComputed ?? 0),
        excusedOtherReal: Number(p.days?.excusedOtherReal ?? 0),
        excusedOtherComputed: Number(p.days?.excusedOtherComputed ?? 0)
      },
      hours: {
        unexcused: Number(p.hours?.unexcused ?? 0),
        excused: Number(p.hours?.excused ?? 0)
      },
      absenceHoursTotal: Number(p.absenceHoursTotal ?? 0),
      absenceQuotientDays: Number(p.absenceQuotientDays ?? 0)
    }));

    // neu: nach dem Rendern initial nach rechts scrollen
    await scrollChartToRight();
  } catch (e) {
    error.value =
        e?.response?.data?.message || e?.message || 'Diagrammdaten konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

// neu: falls items später erneut kommen (z.B. groupBy-Wechsel), ebenfalls nach rechts
watch(
    () => [props.visible, items.value.length],
    async ([vis, len]) => {
      if (vis && len > 0) {
        await scrollChartToRight();
      }
    }
);

function emitClose() {
  emit('close');
}

watch(
    () => [props.visible, props.studentId, props.from, props.to, props.schoolYearCode],
    () => {
      if (props.visible) {
        load();
        loadCalendar();
      }
    },
    {immediate: true}
);

onMounted(() => {
  if (props.visible) {
    load();
    loadCalendar();
  }
});
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.dialog {
  width: min(100%, 1060px);
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .15);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.dialog-header,
.dialog-footer {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dialog-footer {
  border-top: 1px solid var(--border);
  border-bottom: none;
  justify-content: flex-end;
}

.dialog-body {
  padding: 12px;
  overflow: auto;
}

.title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.meta {
  font-size: 12px;
}

.mt-12 {
  margin-top: 12px;
}

.muted {
  color: var(--muted);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 10px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.group-toggle {
  display: inline-flex;
  gap: 6px;
}

.chart-wrapper {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px 25px 12px;
  background: var(--surface);

  /* neu: mehr Platz unterhalb des Plots für X-Labels */
  padding-bottom: 10px;
}

/* X-Achse als Linie unter dem Plot */
.plot-area {
  border-bottom: 1px solid var(--border-strong);

  /* geändert: Baseline bleibt wieder auf gleicher Höhe wie die Y-Skala */
  padding-bottom: 8px;

  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
}

.legend {
  display: grid;
  grid-template-columns: 80px 150px 150px 120px;
  gap: 8px 12px;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 10px;
  padding-left: 80px;
}

.legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 2px;
  margin-right: 6px;
  vertical-align: -1px;
}

/* Farben mit Token-Basis */
.sw-total {
  background: #ccc;
}

.sw-total-comp {
  background: color-mix(in srgb, var(--primary) 40%, #fff 60%);
}

.sw-unexcused {
  background: var(--danger);
}

.sw-unexcused-comp {
  background: color-mix(in srgb, var(--danger) 40%, #fff 60%);
}

.sw-ill {
  background: var(--success);
}

.sw-ill-comp {
  background: color-mix(in srgb, var(--success) 40%, #fff 60%);
}

.sw-other {
  background: var(--warn);
}

.sw-other-comp {
  background: color-mix(in srgb, var(--warn) 40%, #fff 60%);
}

/* Achsen-Layout */
.chart-area {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 12px;
  align-items: end;
}

.y-axis {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: 34px;
}

.y-title {
  font-size: 12px;
  line-height: 14px;
  margin-bottom: 6px;
}

.y-scale {
  position: relative;
  width: 60px;
  border-right: 1px solid var(--border-strong);
}

.y-tick {
  position: absolute;
  left: 30px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
}

.y-tick-label {
  font-size: 12px;
  color: var(--muted);
}

.y-tick-mark {
  width: 8px;
  height: 1px;
  background: var(--border-strong);
}

/* X-Achse als Linie unter dem Plot */
.plot-area {
  border-bottom: 1px solid var(--border-strong);
  /* neu: Platz für X-Labels unterhalb der Plot-Linie */
  padding-bottom: 18px;
  overflow-x: scroll;
  /* geändert: Labels nicht vertikal abschneiden */
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
}

.chart-grid {
  /* neu: als "Spaltenband" layouten, das bei Bedarf breiter als der Viewport wird */
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(120px, 1fr);
  gap: 18px;
  align-items: end;
  width: max-content;
  min-width: 100%;
}

.col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 0;

  /* neu: Anker für absolut positioniertes Label */
  position: relative;
}

.pair-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.pair {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
}

.bar {
  display: flex;
  flex-direction: column-reverse;
  /*background: var(--bg);
  border: 1px solid var(--border);*/
  border-bottom: 1px solid var(--border);
  overflow: hidden;
}

.bar-thin {
  width: 10px;
  height: 100%;
}

.seg {
  width: 100%;
}

/* Segmentfarben passend zur Legende */
.seg-total {
  background: #ccc;
}

.seg-total-comp {
  background: color-mix(in srgb, #ccc 60%, #fff 40%);
}

.seg-unexcused {
  background: var(--danger);
}

.seg-unexcused-comp {
  background: color-mix(in srgb, var(--danger) 60%, #fff 40%);
}

.seg-ill {
  background: var(--success);
}

.seg-ill-comp {
  background: color-mix(in srgb, var(--success) 40%, #fff 60%);
}

.seg-other {
  background: var(--warn);
}

.seg-other-comp {
  background: color-mix(in srgb, var(--warn) 40%, #fff 60%);
}

.x-label {
  text-align: center;
  padding-top: 0;

  /* neu: Label aus dem Flow nehmen, damit die Baseline (0) stimmt */
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 0;

  /* optional: verhindert, dass das Label Scroll/Clicks stört */
  pointer-events: none;
}

.label-main {
  font-size: 13px;
  line-height: 16px;
}

.label-meta {
  font-size: 12px;
  line-height: 14px;
}

.btn {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
}

.notice {
  border-radius: 6px;
  border: 1px solid var(--border);
  padding: 10px 12px;
  background: var(--surface);
}

.notice-danger {
  background: color-mix(in srgb, var(--danger) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
}

/* Kalenderansicht */
.calendar-wrapper {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px 12px;
  background: var(--surface);
}

.cal-legend {
  display: inline-grid;
  grid-auto-flow: row;
  gap: 8px 12px;
  font-size: 12px;
  color: var(--muted);
}

@media (min-width: 720px) {
  .cal-legend {
    grid-auto-flow: column;
  }
}

.legend-swatch.cal-full {
  background: color-mix(in srgb, var(--danger) 12%, #fff 88%);
}

.legend-swatch.cal-partial {
  background: transparent;
  border: 2px solid var(--border-strong);
  box-sizing: border-box;
}

.legend-swatch.cal-unexcused.cal-full {
  background: color-mix(in srgb, var(--danger) 30%, #fff 70%);
  border: 1px solid color-mix(in srgb, var(--danger) 80%, #fff 20%);
}

.legend-swatch.cal-excused.cal-full {
  background: color-mix(in srgb, var(--success) 30%, #fff 70%);
  border: 1px solid color-mix(in srgb, var(--success) 80%, #fff 20%);
}

.legend-swatch.cal-unexcused.cal-partial {
  border-color: color-mix(in srgb, var(--danger) 80%, #fff 20%);
}

.legend-swatch.cal-excused.cal-partial {
  border-color: color-mix(in srgb, var(--success) 80%, #fff 20%);
}

.cal-months {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.cal-month {
  display: flex;
  flex-direction: column;
  padding: 4px;
  background: #f6f6f6;
  border-radius: 6px;
}

.cal-header {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

/* Neuer Wrapper: Kalenderwochen + Raster */
.cal-body {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

/* Spalte für Kalenderwochen */
.cal-weeknums {
  display: grid;
  grid-auto-rows: 28px;
  row-gap: 4px;
  align-items: center;
  justify-items: center;
  font-size: 11px;
  color: var(--muted);
  padding-right: 2px;
  margin-top: -13px;
}

.cal-weeknum-header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  visibility: hidden;
}

.cal-weeknum-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Bisheriger Monatsraster-Bereich */
.cal-main {
  flex: 1 1 auto;
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  font-size: 12px;
  color: var(--muted);
}

.cal-weekdays > span {
  text-align: center;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  grid-auto-rows: 28px;
}

.cal-day {
  height: 26px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text);
}

.cal-day.pad {
  background: transparent;
  border: none;
}

.cal-day.is-full {
  background: var(--surface);
  border-color: var(--border);
}

.cal-day.is-partial {
  border-width: 2px;
  border-style: solid;
  border-color: var(--border-strong);
}

.cal-day.is-full.is-unexcused {
  background: color-mix(in srgb, var(--danger) 30%, #fff 70%);
  border-color: color-mix(in srgb, var(--danger) 80%, #fff 20%);
}

.cal-day.is-full.is-excused {
  background: color-mix(in srgb, var(--success) 30%, #fff 70%);
  border-color: color-mix(in srgb, var(--success) 80%, #fff 20%);
}

.cal-day.is-partial.is-unexcused {
  border-color: color-mix(in srgb, var(--danger) 80%, #fff 20%);
}

.cal-day.is-partial.is-excused {
  border-color: color-mix(in srgb, var(--success) 80%, #fff 20%);
}

.cal-day.is-full.is-partial.is-unexcused {
  border-color: color-mix(in srgb, var(--danger) 80%, #fff 20%);
}

.cal-day.is-full.is-partial.is-excused {
  border-color: color-mix(in srgb, var(--success) 80%, #fff 20%);
}

.cal-day-num {
  font-size: 13px;
  line-height: 16px;
}
</style>